import React from 'react';
import styled from 'styled-components';
import { BarChart3 } from 'lucide-react';
import { StatusDistribution } from '../../services/dashboardService';

interface StatusDistributionCardProps {
  title: string;
  statusDistribution: StatusDistribution[];
  icon?: React.ReactNode;
}

const CardContainer = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  padding: 28px;
  height: 100%;
  display: flex;
  flex-direction: column;

  @media (max-width: 767px) {
    padding: 16px;
    border-radius: 12px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 16px;
`;

const CardIcon = styled.div`
  display: flex;
  align-items: center;
  color: var(--primary-color);
  filter: saturate(0.7) brightness(0.9);
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
`;

const StatusList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
`;

const StatusItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: var(--background-color);
  border-radius: 10px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateX(-4px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  }
`;

const StatusInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const StatusColor = styled.div<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => props.$color};
  flex-shrink: 0;
  filter: saturate(0.7) brightness(0.9);
`;

const StatusName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  flex: 1;
`;

const StatusCount = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
  margin-left: 8px;
`;

const StatusPercentage = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
  padding: 4px 10px;
  background: white;
  border-radius: 12px;
  font-weight: 500;
`;

const ProgressBarContainer = styled.div`
  margin-top: 20px;
  padding-top: 16px;
`;

const ProgressBarTitle = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  margin-bottom: 10px;
  font-weight: 500;
`;

const ProgressBar = styled.div`
  height: 20px;
  background: var(--background-color);
  border-radius: 10px;
  overflow: hidden;
  display: flex;
`;

const ProgressSegment = styled.div<{ $color: string; $width: number }>`
  height: 100%;
  background: ${props => props.$color};
  width: ${props => props.$width}%;
  transition: width 0.3s ease;
  position: relative;
  filter: saturate(0.7) brightness(0.9);

  &:hover {
    opacity: 0.85;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px;
  color: var(--text-secondary);
  font-size: 14px;
`;

const TotalCount = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  background: #f8fafc;
  border-radius: 10px;
  margin-top: 16px;
`;

const TotalLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
`;

const TotalValue = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: var(--primary-color);
  filter: saturate(0.7) brightness(0.9);
`;

const StatusDistributionCard: React.FC<StatusDistributionCardProps> = ({
  title,
  statusDistribution,
  icon
}) => {
  const totalCount = statusDistribution.reduce((sum, status) => sum + status.count, 0);

  if (!statusDistribution || statusDistribution.length === 0) {
    return (
      <CardContainer>
        <CardHeader>
          <CardIcon>
            {icon || <BarChart3 size={20} />}
          </CardIcon>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <EmptyState>אין נתונים להצגה</EmptyState>
      </CardContainer>
    );
  }

  // Sort by count descending
  const sortedStatuses = [...statusDistribution].sort((a, b) => b.count - a.count);

  return (
    <CardContainer>
      <CardHeader>
        <CardIcon>
          {icon || <BarChart3 size={20} />}
        </CardIcon>
        <CardTitle>{title}</CardTitle>
      </CardHeader>

      <StatusList>
        {sortedStatuses.slice(0, 5).map((status) => (
          <StatusItem key={status.statusId}>
            <StatusInfo>
              <StatusColor $color={status.statusColor} />
              <StatusName>{status.statusName}</StatusName>
            </StatusInfo>
            <StatusCount>{status.count}</StatusCount>
            <StatusPercentage>{status.percentage.toFixed(1)}%</StatusPercentage>
          </StatusItem>
        ))}
      </StatusList>

      {sortedStatuses.length > 5 && (
        <StatusList style={{ marginTop: '8px' }}>
          <StatusItem style={{ background: 'transparent', padding: '8px 14px' }}>
            <StatusName style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              +{sortedStatuses.length - 5} סטטוסים נוספים
            </StatusName>
            <StatusCount style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
              {sortedStatuses.slice(5).reduce((sum, s) => sum + s.count, 0)}
            </StatusCount>
          </StatusItem>
        </StatusList>
      )}

      <TotalCount>
        <TotalLabel>סה"כ</TotalLabel>
        <TotalValue>{totalCount}</TotalValue>
      </TotalCount>

      <ProgressBarContainer>
        <ProgressBarTitle>התפלגות ויזואלית</ProgressBarTitle>
        <ProgressBar>
          {sortedStatuses.map((status) => (
            <ProgressSegment
              key={status.statusId}
              $color={status.statusColor}
              $width={status.percentage}
              title={`${status.statusName}: ${status.count} (${status.percentage.toFixed(1)}%)`}
            />
          ))}
        </ProgressBar>
      </ProgressBarContainer>
    </CardContainer>
  );
};

export default StatusDistributionCard;
